import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  type ReactNode,
} from 'react';
import { authService } from '../services/auth';
import { setAuthFailureHandler } from '../services/api/client';
import type { User, AuthState } from '../services/types';

interface AuthContextType extends AuthState {
  login: () => void;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const lastRefreshRef = useRef(0);

  const refreshUser = useCallback(async () => {
    let currentUser = await authService.getCurrentUser();
    if (!currentUser) {
      const refreshed = await authService.refresh();
      if (refreshed) {
        currentUser = await authService.getCurrentUser();
      }
    }
    setUser(currentUser);
  }, []);

  // Register auth failure handler (RC3: API 401 + refresh fail → clear user)
  useEffect(() => {
    setAuthFailureHandler(() => setUser(null));
    return () => setAuthFailureHandler(() => {});
  }, []);

  useEffect(() => {
    let mounted = true;

    // eslint-disable-next-line react-hooks/set-state-in-effect
    refreshUser().finally(() => {
      if (mounted) {
        setIsLoading(false);
      }
    });

    return () => {
      mounted = false;
    };
  }, [refreshUser]);

  // Setup token refresh interval (every 10 minutes) (RC2: clear user on failure)
  useEffect(() => {
    if (!user) return;

    const interval = setInterval(async () => {
      const ok = await authService.refresh();
      if (!ok) setUser(null);
    }, 10 * 60 * 1000);

    return () => clearInterval(interval);
  }, [user]);

  // RC4: Refresh on tab visibility change (debounced 1 min)
  useEffect(() => {
    if (!user) return;

    const handleVisibilityChange = async () => {
      if (document.visibilityState !== 'visible') return;
      const now = Date.now();
      if (now - lastRefreshRef.current < 60_000) return;
      lastRefreshRef.current = now;
      const ok = await authService.refresh();
      if (!ok) setUser(null);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [user]);

  const login = () => {
    window.location.href = authService.getLoginUrl();
  };

  const logout = async () => {
    await authService.logout();
    setUser(null);
  };

  const value: AuthContextType = {
    user,
    isLoading,
    isAuthenticated: !!user,
    isApproved: user?.status === 'approved',
    isAdmin: user?.role === 'admin',
    login,
    logout,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
